import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, of } from 'rxjs'
import { Backend } from 'src/app/shared/enums/enums'
import { DdcLoggerService } from '../../service/log/ddc-logger.service'
import { CanvasData } from './models/canvas.model'

@Injectable({
  providedIn: 'root',
})
export class CanvasService {
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    withCredentials: true,
  }

  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {}

  get_canvases(): Observable<any> {
    this.logger.info('CANVAS | get_canvases')

    return this.http.get(`${Backend.HOST_CONTAINER + Backend.CANVAS}`, this.httpOptions).pipe(
      map((res: any) => {
        return { success: true, res: res.results ?? res }
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('CANVAS | get_canvases | catchError | errorMessage: ' + errorMessage)
        return of({ success: false, res: errorMessage })
      }),
    )
  }

  get_canvas(canvas_id: number): Observable<any> {
    this.logger.info('CANVAS | get_canvas | canvas_id: ' + canvas_id)

    return this.http
      .get(`${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/`, this.httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | get_canvas | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  create_canvas(title: string, description?: string): Observable<any> {
    this.logger.info('CANVAS | create_canvas | title: ' + title)

    const body: any = {
      title: title,
      data: { nodes: [], edges: [], metadata: {} },
    }
    if (description) {
      body.description = description
    }

    return this.http
      .post(`${Backend.HOST_CONTAINER + Backend.CANVAS}`, body, this.httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | create_canvas | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  update_canvas(canvas_id: number, data: CanvasData): Observable<any> {
    this.logger.info('CANVAS | update_canvas | canvas_id: ' + canvas_id)

    return this.http
      .patch(
        `${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/`,
        { data: data },
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | update_canvas | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  rename_canvas(canvas_id: number, title: string): Observable<any> {
    this.logger.info('CANVAS | rename_canvas | canvas_id: ' + canvas_id + ' | title: ' + title)

    return this.http
      .patch(`${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/`, { title }, this.httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | rename_canvas | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  delete_canvas(canvas_id: number): Observable<any> {
    this.logger.info('CANVAS | delete_canvas | canvas_id: ' + canvas_id)

    return this.http
      .delete(`${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/`, this.httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | delete_canvas | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  // --- Canvas Run ---

  run_canvas(canvas_id: number): Observable<any> {
    this.logger.info('CANVAS | run_canvas | canvas_id: ' + canvas_id)

    return this.http
      .post(`${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/run/`, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error?.error || error.error
          this.logger.error('CANVAS | run_canvas | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  // --- Canvas Traces ---

  get_traces(canvas_id: number): Observable<any> {
    this.logger.info('CANVAS | get_traces | canvas_id: ' + canvas_id)

    return this.http
      .get(`${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/traces/`, this.httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | get_traces | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  get_trace(canvas_id: number, trace_id: number): Observable<any> {
    this.logger.info('CANVAS | get_trace | trace_id: ' + trace_id)

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/traces/${trace_id}/`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | get_trace | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  delete_trace(canvas_id: number, trace_id: number): Observable<any> {
    this.logger.info('CANVAS | delete_trace | trace_id: ' + trace_id)

    return this.http
      .delete(
        `${Backend.HOST_CONTAINER + Backend.CANVAS}${canvas_id}/traces/${trace_id}/delete/`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('CANVAS | delete_trace | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }
}
